import React from "react";

// Minimal, safe markdown renderer for assistant messages.
// Supports inline: **bold**, *italic*, _italic_, `code`, [text](url).
// Block-level: paragraphs (split on blank lines) and single-newline line breaks.
// Deliberately NOT using dangerouslySetInnerHTML — all output is React elements,
// so content is escaped and safe from XSS.

const codeStyle = {
  background: "rgba(0, 0, 0, 0.06)",
  padding: "1px 5px",
  borderRadius: "3px",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  fontSize: "0.85em",
};

const linkStyle = {
  color: "#c4622d",
  textDecoration: "underline",
};

function safeHref(url) {
  const trimmed = String(url).trim();
  if (/^(https?:|mailto:|\/|#)/i.test(trimmed)) return trimmed;
  return "#";
}

// Matches one of: **bold**, *italic*, _italic_, `code`, [text](url).
// Non-greedy, single-line tokens only — multi-line formatting is not supported.
const TOKEN_RE = /(\*\*[^*\n]+?\*\*|\*[^*\n]+?\*|_[^_\n]+?_|`[^`\n]+?`|\[[^\]\n]+?\]\([^)\n]+?\))/;

function renderInline(text, keyPrefix) {
  const parts = [];
  let remaining = text;
  let i = 0;

  while (remaining.length > 0) {
    const match = TOKEN_RE.exec(remaining);
    if (!match) {
      parts.push(remaining);
      break;
    }
    const before = remaining.slice(0, match.index);
    if (before) parts.push(before);

    const token = match[0];
    const k = `${keyPrefix}-${i++}`;
    if (token.startsWith("**")) {
      parts.push(<strong key={k}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("*")) {
      parts.push(<em key={k}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith("_")) {
      parts.push(<em key={k}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith("`")) {
      parts.push(
        <code key={k} style={codeStyle}>
          {token.slice(1, -1)}
        </code>
      );
    } else {
      const linkMatch = /\[([^\]]+?)\]\(([^)]+?)\)/.exec(token);
      parts.push(
        <a
          key={k}
          href={safeHref(linkMatch[2])}
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
        >
          {linkMatch[1]}
        </a>
      );
    }
    remaining = remaining.slice(match.index + token.length);
  }

  return parts;
}

export default function Markdown({ children }) {
  const text = String(children ?? "");
  if (!text) return null;

  const paragraphs = text.split(/\n{2,}/);
  return (
    <>
      {paragraphs.map((para, i) => {
        const lines = para.split("\n");
        return (
          <p key={i} style={{ margin: "0 0 8px 0" }}>
            {lines.map((line, j) => (
              <React.Fragment key={j}>
                {renderInline(line, `${i}-${j}`)}
                {j < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}
